/**
 * @file ClientView.js
 * @description Screen that handles a single client record tabs.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '../../store/AuthContext';
import styles from '../individual/IndividualPage.module.scss';
import Spinner from '../ui/spinners/Spinner';
import ClientDetailsTab from './ClientDetailsTab';
import ClientOptionsTab from './ClientOptionsTab';
import ClientContactsTab from './ClientContactsTab';
import ClientUsersTab from './ClientUsersTab';
import ClientSummaryTab from './ClientSummaryTab';
import { getClient } from '../../services/clientService';
import { ROUTES } from '../../constants/routes';
import { SecurityAttributes } from '../../utils/data/enums';
import { getText } from '../../i18n';

const TABS = {
    SUMMARY: 'Summary',
    DETAILS: 'Details',
    CLIENT_OPTIONS: 'Client Options',
    CONTACTS: 'Contacts',
    USERS: 'Users',
};

const ClientView = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { clientId } = useParams();
    const [activeTab, setActiveTab] = useState(TABS.SUMMARY);
    const [individualRecord, setIndividualRecord] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const { multitenantFlag, hasSecurityAttribute } = useAuthContext();
    const canViewClientOptions = hasSecurityAttribute([
        SecurityAttributes.ClientOptionsFullAccess,
        SecurityAttributes.ClientOptionsReadOnly,
    ]);
    const visibleTabs = canViewClientOptions
        ? Object.values(TABS)
        : Object.values(TABS).filter(tab => tab !== TABS.CLIENT_OPTIONS);

    const clearState = () => {
        navigate('.', { state: undefined });
    };

    useEffect(() => {
        if (location.state && location.state.existingTab) {
            setActiveTab(location.state.existingTab);
        } else {
            setActiveTab(TABS.SUMMARY);
        }
    }, []);

    useEffect(() => {
        clearState();
    }, [activeTab]);

    async function fetchIndividualData() {
        try {
            setIsLoading(true);
            const data = await getClient(clientId);
            setIndividualRecord(data);
            setError(null);
        } catch (error) {
            console.error('Error retrieving individual record:', error);
            setError(
                'Could not fetch individual record. Please try again later.',
            );
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (clientId) {
            fetchIndividualData();
        }
    }, [clientId]);

    const returnToSearch = () => {
        // Users who have the multitenantFlag have no home page for now - the '/' route brings them to the Clients tab
        // Once the Multi Tenant dashboard work is complete, then the '/' route will bring them to the Multi Tenant dashboard (component MultiTenantHomePage.js)
        // See ticket/spec for more info - dev.azure.com/MMADevOps/MMA-SW-WEB-INGAGED/_workitems/edit/43470
        navigate(multitenantFlag ? ROUTES.ROOT : ROUTES.CLIENTS);
    };

    const refreshIndividualData = () => {
        fetchIndividualData();
    };

    if (error) {
        return <p>{error}</p>;
    }

    return (
        <div className={`page-content ${styles.container}`}>
            <div className="card">
                <div className="card-body px-0 py-4">
                    <div className="d-flex justify-content-between align-items-center border-bottom position-relative">
                        <ul className={` col-md-8 col-lg-8 ${styles.tabList}`}>
                            {visibleTabs.map(tab => (
                                <li
                                    key={tab}
                                    className={`${styles.tabItem} ${
                                        activeTab === tab ? styles.active : ''
                                    }`}
                                    onClick={() => setActiveTab(tab)}
                                >
                                    {tab}
                                </li>
                            ))}
                        </ul>

                        <div className="end-0 position-absolute top-0">
                            <span
                                type="button"
                                onClick={() => returnToSearch()}
                                className="ing-text-buttons mx-3 ms-3 me-4"
                            >
                                <i className="bi bi-x me-2" />
                                {getText('msg_bbfa773e5a63')}
                            </span>
                        </div>
                    </div>

                    <div className="table-responsive-xxl w-100 mt-5">
                        {isLoading ? (
                            <Spinner
                                width={50}
                                className={styles.loadingSpinner}
                            />
                        ) : (
                            <>
                                <div>
                                    {activeTab === TABS.SUMMARY && (
                                        <ClientSummaryTab
                                            individualId={clientId}
                                            individualRecord={individualRecord}
                                        />
                                    )}
                                    {activeTab === TABS.DETAILS && (
                                        <ClientDetailsTab
                                            individualId={clientId}
                                            individualRecord={individualRecord}
                                            onSave={refreshIndividualData}
                                        />
                                    )}
                                    {canViewClientOptions &&
                                        activeTab === TABS.CLIENT_OPTIONS && (
                                            <ClientOptionsTab
                                                individualId={clientId}
                                                clientRecord={individualRecord}
                                            />
                                        )}
                                    {activeTab === TABS.CONTACTS && (
                                        <ClientContactsTab
                                            individualId={clientId}
                                            individualRecord={individualRecord}
                                            onSave={refreshIndividualData}
                                        />
                                    )}
                                    {activeTab === TABS.USERS && (
                                        <ClientUsersTab
                                            individualId={clientId}
                                            individualRecord={individualRecord}
                                            onSave={refreshIndividualData}
                                        />
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClientView;
